import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Bell, Search, FilePlus, ArrowDown, ArrowUp, MoreHorizontal, X, Globe, CreditCard } from 'lucide-react';
import { Screen } from '../../types';
import { PhoneFrame } from '../PhoneFrame';
import { VirtualAccountApiDisplay } from '../VirtualAccountApiDisplay';
import EthLogo from '../../assets/ethereum-eth-logo.svg';
import BtcLogo from '../../assets/bitcoin-btc-logo.svg';
import UsdcLogo from '../../assets/usd-coin-usdc-logo.svg';
import PkoLogo from '../../assets/pko.svg';

interface FundingScreenProps {
  screen: Screen;
  onTabChange?: (activeTab: string) => void;
}

const iconMap = {
  'file-plus': FilePlus,
  'arrow-down': ArrowDown,
  'arrow-up': ArrowUp,
  'more-horizontal': MoreHorizontal
};

export const FundingScreen: React.FC<FundingScreenProps> = ({ screen, onTabChange }) => {
  const [activeTab, setActiveTabState] = useState<'create-account' | 'eur-deposit' | 'home-screen' | 'crypto-wallet' | 'receive-eth'>('create-account');
  const [activeApiTab, setActiveApiTab] = useState<'request' | 'response'>('request');

  // Custom setActiveTab function that notifies parent
  const setActiveTab = (tab: 'create-account' | 'eur-deposit' | 'home-screen' | 'crypto-wallet' | 'receive-eth') => {
    setActiveTabState(tab);
    onTabChange?.(tab);
  };

  // Notify parent of initial tab state
  useEffect(() => {
    onTabChange?.(activeTab);
  }, [activeTab, onTabChange]);

  const renderCreateVirtualAccountContent = () => (
    <div className="p-6 h-full flex flex-col">
      {/* Top Navigation */}
      <div className="flex items-center justify-between mb-6">
        <ArrowLeft className="w-5 h-5 text-gray-600" />
        <div className="flex items-center space-x-3">
          <Bell className="w-5 h-5 text-gray-600" />
          <Search className="w-5 h-5 text-gray-600" />
        </div>
      </div>

      {/* Virtual Accounts */}
      <div className="space-y-4 mb-6">
        {screen.virtualAccounts?.map((account, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
            className="bg-gray-50 rounded-lg p-4"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {account.type}
            </h3>
            
            {account.accountNumber && (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Account Number:</span>
                  <span className="font-mono text-gray-900">{account.accountNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Routing Number:</span>
                  <span className="font-mono text-gray-900">{account.routingNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Beneficiary Name:</span>
                  <span className="text-gray-900">{account.beneficiaryName}</span>
                </div>
              </div>
            )}
            
            {account.iban && (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">IBAN:</span>
                  <span className="font-mono text-gray-900">{account.iban}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">BIC:</span>
                  <span className="font-mono text-gray-900">{account.bic}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Beneficiary Name:</span>
                  <span className="text-gray-900">{account.beneficiaryName}</span>
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Action Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="grid grid-cols-4 gap-4 mb-6"
      >
        {screen.actions?.map((action) => {
          const IconComponent = iconMap[action.icon as keyof typeof iconMap] || MoreHorizontal;
          const isDeposit = action.name === "Deposit";
          
          return (
            <motion.button
              key={action.name}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={isDeposit ? () => setActiveTab('eur-deposit') : undefined}
              className={`flex flex-col items-center space-y-2 p-2 ${
                isDeposit ? 'cursor-pointer' : ''
              }`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isDeposit 
                  ? 'bg-blue-100 border-2 border-blue-500' 
                  : 'bg-gray-100'
              }`}>
                <IconComponent className={`w-6 h-6 ${
                  isDeposit ? 'text-blue-600' : 'text-gray-600'
                }`} />
              </div>
              <span className={`text-xs text-center ${
                isDeposit ? 'text-blue-600 font-medium' : 'text-gray-600'
              }`}>
                {action.name}
              </span>
            </motion.button>
          );
        })}
      </motion.div>

      {/* Transaction History */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8 }}
        className="flex-1"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Transaction History
        </h3>
        <div className="text-center text-gray-500 py-8">
          {screen.depositHistory}
        </div>
      </motion.div>
    </div>
  );

  const renderEurDepositContent = () => (
    <div className="p-4 h-full flex flex-col">
      {/* SEPA Transfer Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="flex-1 flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-lg font-bold text-black">SEPA Transfer</h1>
          <X className="w-4 h-4 text-gray-600" />
        </div>

        {/* From Section */}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">From</label>
          <div className="bg-gray-50 rounded-lg p-2">
            <div className="flex items-center space-x-2 mb-1">
              <div className="w-5 h-5 bg-blue-600 rounded flex items-center justify-center">
                <img src={PkoLogo} alt="PKO" className="w-6 h-6" />
              </div>
              <div>
                <div className="font-medium text-gray-900 text-xs">{screen.sepaTransfer?.from.bank}</div>
                <div className="text-xs text-gray-600">{screen.sepaTransfer?.from.balance}</div>
              </div>
            </div>
            <button className="text-blue-600 text-xs font-medium">Change</button>
          </div>
        </div>

        {/* To Section */}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">To</label>
          <div className="space-y-1.5">
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Beneficiary Name</label>
              <input
                type="text"
                value={screen.sepaTransfer?.to.beneficiaryName || ''}
                readOnly
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md bg-gray-50 text-gray-900"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">IBAN</label>
              <input
                type="text"
                value={screen.sepaTransfer?.to.iban || ''}
                readOnly
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md bg-gray-50 text-gray-900"
              />
            </div>
          </div>
        </div>

        {/* Amount Section */}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">Amount</label>
          <input
            type="text"
            value={screen.sepaTransfer?.amount || ''}
            readOnly
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md bg-gray-50 text-gray-900 font-medium"
          />
        </div>

        {/* Memo Section */}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">Memo</label>
          <input
            type="text"
            value={screen.sepaTransfer?.memo || ''}
            readOnly
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md bg-gray-50 text-gray-900"
          />
        </div>

        {/* Review Transfer Button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setActiveTab('home-screen')}
          className="w-full bg-blue-600 text-white py-2.5 px-3 rounded-lg font-semibold text-xs mt-auto cursor-pointer"
        >
          Send Transfer
        </motion.button>
      </motion.div>
    </div>
  );

  const renderHomeScreenContent = () => (
    <div className="p-6 h-full flex flex-col">
      {/* Top Navigation */}
      <div className="flex items-center justify-between mb-6">
        <ArrowLeft className="w-5 h-5 text-gray-600" />
        <div className="flex items-center space-x-3">
          <Bell className="w-5 h-5 text-gray-600" />
          <Search className="w-5 h-5 text-gray-600" />
        </div>
      </div>

      {/* Primary Account Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="bg-blue-50 rounded-lg p-4 mb-6"
      >
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-gray-900">Primary Account</h2>
        </div>
        
        <div className="text-3xl font-bold text-gray-900 mb-2">$115.00</div>
        
        <div className="text-sm text-gray-600 mb-3">Balance</div>
        
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">USDC Rewards (4.0% APY)</span>
          <span className="text-sm text-green-600 font-medium">$0.01</span>
        </div>
      </motion.div>

      {/* Action Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="grid grid-cols-5 gap-4 mb-6"
      >
        {[
          { name: "Virtual Account", icon: ArrowDown },
          { name: "Wallet", icon: Globe },
          { name: "Pay", icon: ArrowUp },
          { name: "Card", icon: CreditCard },
          { name: "More", icon: MoreHorizontal }
        ].map((action) => {
          const isWallet = action.name === "Wallet";
          
          return (
            <motion.button
              key={action.name}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={isWallet ? () => setActiveTab('crypto-wallet') : undefined}
              className={`flex flex-col items-center space-y-2 p-2 ${
                isWallet ? 'cursor-pointer' : ''
              }`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isWallet 
                  ? 'bg-blue-100 border-2 border-blue-500' 
                  : 'bg-gray-100'
              }`}>
                <action.icon className={`w-6 h-6 ${
                  isWallet ? 'text-blue-600' : 'text-gray-600'
                }`} />
              </div>
              <span className={`text-xs text-center ${
                isWallet ? 'text-blue-600 font-medium' : 'text-gray-600'
              }`}>
                {action.name}
              </span>
            </motion.button>
          );
        })}
      </motion.div>

      {/* Transaction History */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="flex-1"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Transaction History</h3>
        
        {/* Recent Transactions */}
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <img src={PkoLogo} alt="PKO" className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">PKO Bank Polski</div>
              <div className="text-xs text-gray-500">SEPA Instant</div>
            </div>
            <div className="text-sm font-semibold text-green-600">+ €100.00</div>
          </div>
        </div>
      </motion.div>
    </div>
  );

  const renderCryptoWalletContent = () => (
    <div className="p-6 h-full flex flex-col">
      {/* Top Navigation */}
      <div className="flex items-center justify-between mb-6">
        <ArrowLeft className="w-5 h-5 text-gray-600" />
        <div className="flex items-center space-x-3">
          <Bell className="w-5 h-5 text-gray-600" />
          <Search className="w-5 h-5 text-gray-600" />
        </div>
      </div>

      {/* Wallet Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="mb-4"
      >
        <h1 className="text-lg font-semibold text-gray-900 mb-1">My Wallet</h1>
        <div className="flex items-center space-x-3">
          <div className="text-2xl font-bold text-gray-900">$115.00</div>
          <div className="text-sm text-blue-600 font-medium">+ 1.17%</div>
        </div>
      </motion.div>

      {/* Chart Area */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg h-24 mb-4 p-3 relative overflow-hidden"
      >
        {/* Abstract Growth Line */}
        <svg 
          className="absolute inset-0 w-full h-full" 
          viewBox="0 0 200 100" 
          preserveAspectRatio="xMidYMid meet"
          style={{ shapeRendering: 'geometricPrecision' }}
        >
          {/* Gradient definition */}
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.8"/>
              <stop offset="50%" stopColor="#1D4ED8" stopOpacity="0.9"/>
              <stop offset="100%" stopColor="#1E40AF" stopOpacity="1"/>
            </linearGradient>
            <linearGradient id="fillGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3"/>
              <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.05"/>
            </linearGradient>
          </defs>
          
          {/* Area fill */}
          <path
            d="M 10 80 Q 30 70 50 60 T 90 40 T 130 25 T 170 15 T 190 10 L 190 100 L 10 100 Z"
            fill="url(#fillGradient)"
          />
          
          {/* Growth line */}
          <path
            d="M 10 80 Q 30 70 50 60 T 90 40 T 130 25 T 170 15 T 190 10"
            stroke="url(#lineGradient)"
            strokeWidth="2.5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          
          {/* Data points */}
          <circle cx="10" cy="80" r="2.5" fill="#3B82F6" stroke="#ffffff" strokeWidth="1" opacity="0.9"/>
          <circle cx="50" cy="60" r="2.5" fill="#3B82F6" stroke="#ffffff" strokeWidth="1" opacity="0.9"/>
          <circle cx="90" cy="40" r="2.5" fill="#3B82F6" stroke="#ffffff" strokeWidth="1" opacity="0.9"/>
          <circle cx="130" cy="25" r="2.5" fill="#3B82F6" stroke="#ffffff" strokeWidth="1" opacity="0.9"/>
          <circle cx="170" cy="15" r="2.5" fill="#3B82F6" stroke="#ffffff" strokeWidth="1" opacity="0.9"/>
          <circle cx="190" cy="10" r="3.5" fill="#1E40AF" stroke="#ffffff" strokeWidth="1.5" opacity="1"/>
        </svg>
        
        {/* Performance indicator 
        <div className="absolute top-3 right-3 flex items-center space-x-1">
          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          <span className="text-xs font-medium text-gray-600">+1.17%</span>
        </div>*/}
      </motion.div>

      {/* Navigation Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="flex space-x-6 mb-3"
      >
        <button className="text-sm font-semibold text-gray-900 border-b-2 border-gray-900 pb-1">
          Assets
        </button>
        <button className="text-sm text-gray-500 pb-1">
          Transfer History
        </button>
      </motion.div>

      {/* Instruction Text 
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8 }}
        className="text-xs text-gray-500 mb-4"
      >
        Select an asset to view details.
      </motion.div>*/}

      {/* Asset List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.0 }}
        className="bg-white rounded-lg border border-gray-200 overflow-hidden flex-1"
      >
        {/* USDC */}
        <div className="flex items-center space-x-3 p-3">
          <div className="w-7 h-7 flex items-center justify-center">
            <img src={UsdcLogo} alt="USDC" className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-900">USDC</div>
          </div>
          <div className="text-sm font-medium text-gray-900">$115.00</div>
        </div>
        
        {/* Ethereum */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setActiveTab('receive-eth')}
          className="flex items-center space-x-3 p-3 bg-blue-50 border-b border-blue-100 w-full cursor-pointer hover:bg-blue-100 transition-colors text-left"
        >
          <div className="w-7 h-7 flex items-center justify-center">
            <img src={EthLogo} alt="ETH" className="w-6 h-6" />
          </div>
          <div className="flex-1 text-left">
            <div className="text-sm font-medium text-blue-900 text-left">ETH</div>
          </div>
          <div className="text-sm font-medium text-blue-900">$0.00</div>
        </motion.button>

        {/* Bitcoin */}
        <div className="flex items-center space-x-3 p-3">
          <div className="w-7 h-7 flex items-center justify-center">
            <img src={BtcLogo} alt="Bitcoin" className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-900">Bitcoin</div>
          </div>
          <div className="text-sm font-medium text-gray-900">$0.00</div>
        </div>
      </motion.div>
    </div>
  );

  const renderReceiveETHContent = () => (
    <div className="p-6 h-full flex flex-col">
      {/* Top Navigation */}
      <div className="flex items-center justify-between mb-6">
        <ArrowLeft className="w-5 h-5 text-gray-600" />
        <div className="flex items-center space-x-3">
          <Bell className="w-5 h-5 text-gray-600" />
          <Search className="w-5 h-5 text-gray-600" />
        </div>
      </div>

      {/* Receive ETH Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="mb-3"
      >
        <div className="flex items-center justify-center space-x-2 mb-1">
          <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
            <img src={EthLogo} alt="ETH" className="w-4 h-4" />
          </div>
          <h1 className="text-lg font-bold text-gray-900">Receive ETH</h1>
        </div>
        <p className="text-xs text-gray-600 text-center">Share your address to receive Ethereum or any ERC20 token</p>
      </motion.div>

      {/* QR Code */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="mb-3 flex justify-center"
      >
        <div className="w-48 h-48 bg-gray-100 rounded-lg flex items-center justify-center">
          <div className="text-center">
            <div className="w-40 h-40 bg-black rounded-lg flex items-center justify-center mb-1">
              <div className="w-36 h-36 bg-white rounded grid grid-cols-10 gap-0.5 p-1">
                {/* Simple QR code pattern */}
                {Array.from({ length: 100 }).map((_, i) => (
                  <div
                    key={i}
                    className={`w-1 h-1 ${Math.random() > 0.5 ? 'bg-black' : 'bg-white'}`}
                  />
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-500">QR Code</p>
          </div>
        </div>
      </motion.div>

      {/* Address */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="mb-3"
      >
        <label className="block text-sm font-medium text-gray-700 mb-2">Your Ethereum Address</label>
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-mono text-gray-900 break-all">
              0x7429...8603
            </span>
            <button className="ml-2 px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 transition-colors">
              Copy
            </button>
          </div>
        </div>
      </motion.div>

      {/* Action Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8 }}
        className="space-y-3 flex-1"
      >
        <button className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors">
          Deposit from Exchange
        </button>
        <button className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium text-sm hover:bg-gray-200 transition-colors">
          Create Hosted Payment Link
        </button>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center px-8 py-16">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Left Side - Mobile Content */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="flex justify-center"
        >
          <PhoneFrame>
            {activeTab === 'create-account' 
              ? renderCreateVirtualAccountContent() 
              : activeTab === 'eur-deposit' 
              ? renderEurDepositContent() 
              : activeTab === 'home-screen'
              ? renderHomeScreenContent()
              : activeTab === 'crypto-wallet'
              ? renderCryptoWalletContent()
              : renderReceiveETHContent()}
          </PhoneFrame>
        </motion.div>

        {/* Right Side - Info Panel */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-6"
        >
          <div>
            <h2 className="text-3xl font-bold text-black mb-4">
              {screen.title}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              {screen.description}
            </p>
          </div>

          {/* Navigation Tabs */}
          <div className="flex space-x-4 mb-4">
            <button
              onClick={() => setActiveTab('create-account')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'create-account'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Create a Virtual Account
            </button>
            <button
              onClick={() => setActiveTab('eur-deposit')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'eur-deposit'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Deposit EUR
            </button>
            <button
              onClick={() => setActiveTab('home-screen')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'home-screen'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Home Screen
            </button>
            <button
              onClick={() => setActiveTab('crypto-wallet')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'crypto-wallet'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Crypto Wallet
            </button>
            <button
              onClick={() => setActiveTab('receive-eth')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'receive-eth'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Receive ETH
            </button>
          </div>

          {/* Content based on active tab */}
          {activeTab === 'create-account' && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <div className="text-sm font-medium text-gray-600">
                    &lt;/&gt; Code
                  </div>
                  <button 
                    onClick={() => setActiveApiTab('request')}
                    className={`px-3 py-1 rounded text-sm ${
                      activeApiTab === 'request' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'text-gray-500'
                    }`}
                  >
                    Request
                  </button>
                  <button 
                    onClick={() => setActiveApiTab('response')}
                    className={`px-3 py-1 rounded text-sm ${
                      activeApiTab === 'response' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'text-gray-500'
                    }`}
                  >
                    Response
                  </button>
                </div>
              </div>

              <VirtualAccountApiDisplay 
                apiRequest={screen.apiRequest} 
                apiResponse={screen.virtualAccountApiResponse}
                activeTab={activeApiTab}
              />
            </div>
          )}

          {activeTab === 'eur-deposit' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="font-semibold text-blue-900 mb-3">SEPA Transfer Details</h3>
              <div className="space-y-2 text-sm text-blue-800">
                <div className="flex justify-between">
                  <span>From:</span>
                  <span className="font-medium">{screen.sepaTransfer?.from.bank}</span>
                </div>
                <div className="flex justify-between">
                  <span>To:</span>
                  <span className="font-medium">{screen.sepaTransfer?.to.beneficiaryName}</span>
                </div>
                <div className="flex justify-between">
                  <span>Amount:</span>
                  <span className="font-medium">{screen.sepaTransfer?.amount}</span>
                </div>
                <div className="flex justify-between">
                  <span>IBAN:</span>
                  <span className="font-medium text-xs">{screen.sepaTransfer?.to.iban}</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'home-screen' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="font-semibold text-green-900 mb-3">Automated Fiat-to-Stablecoin Conversion</h3>
              <div className="space-y-3 text-sm text-green-800">
                <p className="leading-relaxed">
                Deposit EUR, PLN, or USD via local bank rails, and Across instantly converts funds into the stablecoin of your choice (USDC, EURC, or others).
                </p>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Fiat deposit can be mapped to any supported stablecoin</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Real-time conversion with transparent rates</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Optional yield on selected stablecoin holdings</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'crypto-wallet' && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
              <h3 className="font-semibold text-purple-900 mb-3">Full-Stack Infrastructure</h3>
              <div className="space-y-3 text-sm text-purple-800">
                <p className="leading-relaxed">
                  Across provides a complete custody and settlement layer designed for regulated institutions and fintechs.
                </p>
                <div className="space-y-2">
                  <div className="flex items-start space-x-2">
                    <div className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0 mt-1.5"></div>
                    <span><strong>Broad Asset Coverage</strong> — Manage 350+ cryptocurrencies across 23+ blockchains, including Tron, Solana, and Ethereum.</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <div className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0 mt-1.5"></div>
                    <span><strong>Gas-Free Transactions</strong> — We cover and abstract all network fees.</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <div className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0 mt-1.5"></div>
                    <span><strong>MPC-based Security</strong> — Institutional-grade Multi-Party Computation (MPC) ensures private key protection without single points of failure.</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <div className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0 mt-1.5"></div>
                    <span><strong>Scalable Infrastructure</strong> — Built to support high transaction volumes with low latency and enterprise-grade uptime.</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'receive-eth' && (
            <div className="space-y-4">
              {/* Yellow notification box */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center">
                      <span className="text-yellow-800 text-xs font-bold">!</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-yellow-800 mb-2">Auto-Convert Incoming Crypto</h4>
                    <p className="text-sm text-yellow-700 leading-relaxed">
                      Accept any supported digital asset while maintaining stablecoin balances. Across lets you automatically settle incoming payments (e.g. ETH → USDC, BTC → EURC) in real time, so businesses can accept crypto without exposure to price risk.
                    </p>
                  </div>
                </div>
              </div>
            
              <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm font-medium text-gray-600">
                  &lt;/&gt; Monitoring events<span className="loading-dots"></span>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <pre className="text-sm overflow-x-auto">
                  <span className="text-gray-600">{'{'}</span>
                  <br />
                  <span className="text-gray-600">  </span>
                  <span className="text-blue-600">"transactionId"</span>
                  <span className="text-gray-600">: </span>
                  <span className="text-green-600">"1654506149519862"</span>
                  <span className="text-gray-600">,</span>
                  <br />
                  <span className="text-gray-600">  </span>
                  <span className="text-blue-600">"amount"</span>
                  <span className="text-gray-600">: </span>
                  <span className="text-green-600">"0.1"</span>
                  <span className="text-gray-600">,</span>
                  <br />
                  <span className="text-gray-600">  </span>
                  <span className="text-blue-600">"createdAt"</span>
                  <span className="text-gray-600">: </span>
                  <span className="text-green-600">"2025-10-14T17:45:27-04:00"</span>
                  <span className="text-gray-600">,</span>
                  <br />
                  <span className="text-gray-600">  </span>
                  <span className="text-blue-600">"description"</span>
                  <span className="text-gray-600">: </span>
                  <span className="text-green-600">"Deposit"</span>
                  <span className="text-gray-600">,</span>
                  <br />
                  <span className="text-gray-600">  </span>
                  <span className="text-blue-600">"from"</span>
                  <span className="text-gray-600">: </span>
                  <span className="text-green-600">"Vitalik Buterin"</span>
                  <br />
                  <span className="text-gray-600">{'}'}</span>
                </pre>
              </div>
            </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};
